/**
 * GraphPane — Process graph visualization using React Flow.
 *
 * Renders the build plan's process graph as an interactive DAG.
 * Nodes are colored by type, statuses are indicated visually,
 * and clicking a node opens its detail panel.
 */

import { useMemo, useCallback, useState } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MarkerType,
  type Node,
  type Edge,
  type NodeProps,
  Handle,
  Position,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import dagre from '@dagrejs/dagre';
import { cn } from '@/lib/utils';
import type { ProcessGraph, GraphNode as GraphNodeType } from '../types/guided-builder';
import { GraphNodeDetail } from './GraphNodeDetail';

// Node type color schemes (Tailwind classes)
const NODE_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  'user-input': {
    bg: 'bg-blue-50 dark:bg-blue-950/40',
    border: 'border-blue-400 dark:border-blue-600',
    text: 'text-blue-700 dark:text-blue-300',
  },
  'ai-process': {
    bg: 'bg-purple-50 dark:bg-purple-950/40',
    border: 'border-purple-400 dark:border-purple-600',
    text: 'text-purple-700 dark:text-purple-300',
  },
  code: {
    bg: 'bg-green-50 dark:bg-green-950/40',
    border: 'border-green-400 dark:border-green-600',
    text: 'text-green-700 dark:text-green-300',
  },
  'entity-ref': {
    bg: 'bg-orange-50 dark:bg-orange-950/40',
    border: 'border-orange-400 dark:border-orange-600',
    text: 'text-orange-700 dark:text-orange-300',
  },
  'arche-ref': {
    bg: 'bg-pink-50 dark:bg-pink-950/40',
    border: 'border-pink-400 dark:border-pink-600',
    text: 'text-pink-700 dark:text-pink-300',
  },
};

// Status visual modifiers
const STATUS_CLASSES: Record<string, string> = {
  pending: 'opacity-60',
  building: 'ring-2 ring-yellow-400 dark:ring-yellow-500 animate-pulse',
  verified: 'ring-2 ring-green-400 dark:ring-green-500',
};

// Type badges
const TYPE_LABELS: Record<string, string> = {
  'user-input': 'Input',
  'ai-process': 'AI',
  code: 'Code',
  'entity-ref': 'Entity',
  'arche-ref': 'Arche',
};

interface GraphNodeData {
  graphNode: GraphNodeType;
  onNodeClick: (node: GraphNodeType) => void;
  [key: string]: unknown;
}

function ProcessGraphNode({ data }: NodeProps<Node<GraphNodeData>>) {
  const node = data.graphNode;
  const colors = NODE_COLORS[node.type] ?? NODE_COLORS['ai-process'];
  const statusClass = STATUS_CLASSES[node.status] ?? '';

  return (
    <div
      className={cn(
        'px-3 py-2 rounded-lg border-2 shadow-sm cursor-pointer transition-all min-w-[140px] max-w-[200px]',
        colors.bg,
        colors.border,
        statusClass,
      )}
      onClick={() => data.onNodeClick(node)}
    >
      <Handle type="target" position={Position.Left} className="!bg-muted-foreground !w-2 !h-2" />
      <div className="flex items-center gap-1.5 mb-1">
        <span
          className={cn(
            'text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded',
            colors.text,
            colors.bg,
          )}
        >
          {TYPE_LABELS[node.type] ?? node.type}
        </span>
        {node.status === 'verified' && <span className="text-green-500 text-xs">&#10003;</span>}
      </div>
      <p className="text-xs font-medium leading-tight truncate">{node.title}</p>
      {node.inputs.length > 0 && (
        <p className="text-[10px] text-muted-foreground mt-0.5 truncate">
          In: {node.inputs.map((p) => p.label).join(', ')}
        </p>
      )}
      {node.outputs.length > 0 && (
        <p className="text-[10px] text-muted-foreground truncate">
          Out: {node.outputs.map((p) => p.label).join(', ')}
        </p>
      )}
      <Handle type="source" position={Position.Right} className="!bg-muted-foreground !w-2 !h-2" />
    </div>
  );
}

const nodeTypes = { processNode: ProcessGraphNode };

// Use a fixed neutral color for edges/arrows — CSS variables don't work in SVG markers
const EDGE_COLOR = '#94a3b8'; // slate-400, visible in both light and dark themes

const defaultEdgeOptions = {
  type: 'smoothstep' as const,
  style: { stroke: EDGE_COLOR, strokeWidth: 2 },
  markerEnd: {
    type: MarkerType.ArrowClosed,
    width: 16,
    height: 16,
    color: EDGE_COLOR,
  },
};

/**
 * Transitive reduction of a DAG edge list.
 * Removes edges (u, v) where a longer path u → … → v exists,
 * keeping only the direct/necessary edges for a clean visual flow.
 */
function transitiveReduction(edges: Edge[]): Edge[] {
  // Build adjacency list from edges
  const adj = new Map<string, Set<string>>();
  for (const e of edges) {
    if (!adj.has(e.source)) adj.set(e.source, new Set());
    adj.get(e.source)!.add(e.target);
  }

  // For each edge (u, v), check if v is reachable from u through other nodes
  return edges.filter((e) => {
    const { source, target } = e;
    // BFS/DFS from source, but skip the direct edge to target
    const visited = new Set<string>();
    const queue = [...(adj.get(source) ?? [])].filter((n) => n !== target);
    for (const n of queue) visited.add(n);

    while (queue.length > 0) {
      const current = queue.shift()!;
      if (current === target) return false; // alternative path exists — remove edge
      for (const neighbor of adj.get(current) ?? []) {
        if (!visited.has(neighbor)) {
          visited.add(neighbor);
          queue.push(neighbor);
        }
      }
    }
    return true; // no alternative path — keep edge
  });
}

/**
 * Auto-layout graph nodes using dagre.
 * Returns nodes with updated positions.
 */
function layoutGraph(nodes: Node<GraphNodeData>[], edges: Edge[]): Node<GraphNodeData>[] {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: 'LR', nodesep: 80, ranksep: 160 });

  for (const node of nodes) {
    g.setNode(node.id, { width: 180, height: 80 });
  }
  for (const edge of edges) {
    g.setEdge(edge.source, edge.target);
  }

  dagre.layout(g);

  return nodes.map((node) => {
    const pos = g.node(node.id);
    return {
      ...node,
      position: { x: pos.x - 90, y: pos.y - 40 },
    };
  });
}

interface GraphPaneProps {
  processGraph: ProcessGraph | undefined;
  currentNodeId?: string | null;
  compact?: boolean;
}

export function GraphPane({ processGraph, currentNodeId, compact = false }: GraphPaneProps) {
  const [selectedNode, setSelectedNode] = useState<GraphNodeType | null>(null);

  const handleNodeClick = useCallback((node: GraphNodeType) => {
    setSelectedNode(node);
  }, []);

  const { nodes, edges } = useMemo(() => {
    if (!processGraph || processGraph.nodes.length === 0) {
      return { nodes: [], edges: [] };
    }

    const rfNodes: Node<GraphNodeData>[] = processGraph.nodes.map((gn) => ({
      id: gn.id,
      type: 'processNode',
      position: gn.position,
      data: { graphNode: gn, onNodeClick: handleNodeClick },
    }));

    const allEdges: Edge[] = processGraph.edges.map((ge) => ({
      id: ge.id,
      source: ge.source,
      target: ge.target,
      type: 'smoothstep',
      label: ge.label,
      animated: processGraph.nodes.find((n) => n.id === ge.source)?.status === 'building',
    }));

    // Remove transitive edges so only direct flow arrows remain
    const rfEdges = transitiveReduction(allEdges);

    // Use dagre auto-layout (overrides AI positions for consistency)
    const layoutNodes = layoutGraph(rfNodes, rfEdges);

    return { nodes: layoutNodes, edges: rfEdges };
  }, [processGraph, currentNodeId, handleNodeClick]);

  if (!processGraph || processGraph.nodes.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <p className="text-sm">Graph will appear when AI creates a build plan</p>
      </div>
    );
  }

  return (
    <div className={cn('w-full', compact ? 'h-[250px]' : 'h-full')}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        defaultEdgeOptions={defaultEdgeOptions}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        panOnDrag
        zoomOnScroll
        minZoom={0.3}
        maxZoom={1.5}
        proOptions={{ hideAttribution: true }}
      >
        <Background gap={16} size={1} />
        {!compact && <Controls showInteractive={false} />}
      </ReactFlow>

      {selectedNode && !compact && (
        <GraphNodeDetail
          node={selectedNode}
          graph={processGraph}
          onClose={() => setSelectedNode(null)}
        />
      )}
    </div>
  );
}
